// View-layer helpers: the multi-dimensional filter that drives the
// transactions table + summary cards, plus the CSV export that drops
// the current view to disk.
//
// Filter dimensions (all defaulting to "all" = pass-through):
//   month        "all" | "YYYY" | "YYYY-MM"
//   category     "all" | category name
//   subcategory  "all" | "Category / Subcategory" (qualified, since
//                same sub name can exist under multiple cats)
//   portal       "all" | portal id
//   account      "all" | account label
//   kind         "all" | "income" | "expense"
//
// The predicate combines them with AND semantics. Persisting goes
// through state.ts since it touches localStorage, not the data folder.

import type { Transaction } from "./common.js";
import { monthKey } from "./common.js";

export interface ViewFilter {
  month: string;
  category: string;
  subcategory: string;
  portal: string;
  account: string;
  /** Last-4 digits of the card or account number, when the loader extracted
   *  one. Mirrors the desktop dashboard's filter-last4 dropdown. */
  last4: string;
  /** Transaction direction:
   *    income   — negative amounts (money in)
   *    expense  — positive amounts (money out)
   *    transfer — rows categorized as "Transfer" (matches desktop's direction filter)
   *    all      — pass-through. */
  kind: "all" | "income" | "expense" | "transfer";
  /** Soft-delete visibility (mirrors desktop's filter-deleted):
   *    active        — hide tx.deleted rows (default)
   *    all           — include both
   *    only-deleted  — only tx.deleted rows */
  deleted: "active" | "all" | "only-deleted";
}

export function emptyFilter(): ViewFilter {
  return {
    month: "all",
    category: "all",
    subcategory: "all",
    portal: "all",
    account: "all",
    last4: "all",
    kind: "all",
    deleted: "active", // matches desktop default — hide deleted txs
  };
}

export function isFilterDefault(f: ViewFilter): boolean {
  return (
    f.month === "all" &&
    f.category === "all" &&
    f.subcategory === "all" &&
    f.portal === "all" &&
    f.account === "all" &&
    f.last4 === "all" &&
    f.kind === "all" &&
    f.deleted === "active"
  );
}

/** Mirrors the original month-only predicate so existing tests keep
 *  passing. Kept as its own function so we can call it from txMatches. */
export function monthMatches(month: string, filter: string): boolean {
  if (filter === "all") return true;
  if (/^\d{4}$/.test(filter)) return month.startsWith(filter + "-");
  return month === filter;
}

/** Combined AND predicate. Returns true iff the transaction passes
 *  every active filter dimension.
 *
 *  `hiddenSimplefinLabels` — optional set of `tx.account` labels for
 *  SimpleFIN accounts the user has flagged hidden in the manage modal.
 *  Applies retroactively to already-imported rows so toggling hidden
 *  drops them from the view without needing a re-pull. Built by
 *  `hiddenAccountLabels(items)` in simplefin_storage.ts. */
export function txMatches(
  tx: Transaction,
  f: ViewFilter,
  hiddenSimplefinLabels?: Set<string>,
): boolean {
  // Deleted-visibility gate runs FIRST so a deleted tx doesn't survive
  // any other filter combination when the user is in "active" mode.
  if (f.deleted === "active" && tx.deleted) return false;
  if (f.deleted === "only-deleted" && !tx.deleted) return false;
  // Hidden-account gate: strip SimpleFIN rows whose account is currently
  // flagged hidden. Applies regardless of deleted-visibility mode — a
  // hidden account stays hidden even when the user is browsing deleted
  // rows. Only fires on portal=simplefin so non-SimpleFIN portals that
  // happen to share an account label name aren't accidentally filtered.
  if (
    hiddenSimplefinLabels &&
    hiddenSimplefinLabels.size > 0 &&
    tx.portal === "simplefin" &&
    hiddenSimplefinLabels.has(tx.account)
  ) {
    return false;
  }
  if (!monthMatches(monthKey(tx.date), f.month)) return false;
  if (f.category !== "all" && tx.category !== f.category) return false;
  if (f.subcategory !== "all") {
    const qualified = `${tx.category} / ${tx.subcategory}`;
    if (qualified !== f.subcategory) return false;
  }
  if (f.portal !== "all" && tx.portal !== f.portal) return false;
  if (f.account !== "all" && tx.account !== f.account) return false;
  if (f.last4 !== "all" && tx.account_last4 !== f.last4) return false;
  // Sign convention in this app: positive amount = expense, negative = income.
  // Transfers are flagged by category="Transfer" (mirrors desktop dashboard
  // filter-direction semantics). They CAN have either sign; we don't gate on
  // sign for the transfer branch.
  if (f.kind === "income" && tx.amount >= 0) return false;
  if (f.kind === "expense" && tx.amount <= 0) return false;
  if (f.kind === "transfer" && tx.category !== "Transfer") return false;
  return true;
}

/** localStorage round-trip with a graceful fallback when the persisted
 *  blob is corrupt or storage is disabled (private mode etc.). */
export function loadFilter(key: string): ViewFilter {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return emptyFilter();
    const parsed = JSON.parse(raw) as Partial<ViewFilter>;
    return { ...emptyFilter(), ...parsed };
  } catch {
    return emptyFilter();
  }
}

export function saveFilter(key: string, f: ViewFilter): void {
  try {
    localStorage.setItem(key, JSON.stringify(f));
  } catch {
    // private mode / quota — non-fatal.
  }
}

// ─────────────────────────────────────────────────────────────────────
// CSV export of the current filtered view
// ─────────────────────────────────────────────────────────────────────

/** Columns shipped in the CSV. Ordered so the most-frequently-needed
 *  fields are leftmost. Includes everything that's not a hash id —
 *  the hash isn't useful outside the app, omitted intentionally. */
const CSV_COLUMNS = [
  "Date",
  "Portal",
  "Institution",
  "Account",
  "Account Last 4",
  "Account Kind",
  "Vendor",
  "Category",
  "Subcategory",
  "Amount",
  "Source File",
  "Notes",
] as const;

function csvEscape(value: string): string {
  // RFC 4180: only quote when the value contains comma, quote, or newline.
  // Quotes inside the value get doubled.
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/** Render the filtered transactions to a CSV string. Excludes
 *  Transaction.deleted rows; sorts by date descending so the most
 *  recent activity is at the top (matches the table). */
export function transactionsToCsv(txs: Transaction[]): string {
  const lines: string[] = [];
  lines.push(CSV_COLUMNS.join(","));
  const sorted = [...txs]
    .filter((t) => !t.deleted)
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  for (const t of sorted) {
    const row: string[] = [
      t.date,
      t.portal,
      t.institution_name || "",
      t.account || "",
      t.account_last4 || "",
      t.account_kind || "",
      t.vendor_raw,
      t.category,
      t.subcategory,
      // Use a fixed format (no thousands separators, two decimal places)
      // so spreadsheets parse it unambiguously.
      t.amount.toFixed(2),
      t.source_file || "",
      t.notes || "",
    ].map(csvEscape);
    lines.push(row.join(","));
  }
  // Trailing newline is conventional + makes diffs cleaner.
  return lines.join("\n") + "\n";
}

/** Human-readable summary of the active filter for the download filename.
 *  E.g., {month: "2026-04", kind: "expense"} → "2026-04-expense". */
export function filterSummary(f: ViewFilter): string {
  const parts: string[] = [];
  if (f.month !== "all") parts.push(f.month);
  if (f.kind !== "all") parts.push(f.kind);
  if (f.category !== "all") parts.push(slugify(f.category));
  if (f.subcategory !== "all") parts.push(slugify(f.subcategory.split(" / ").pop() || ""));
  if (f.portal !== "all") parts.push(f.portal);
  if (f.account !== "all") parts.push(slugify(f.account));
  return parts.length > 0 ? parts.join("-") : "all";
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);
}

/** Trigger a browser download of the CSV blob. Stays local-only —
 *  same pattern as log.downloadReport: Blob → ObjectURL → anchor.click. */
export function downloadFilteredCsv(txs: Transaction[], filter: ViewFilter): void {
  if (typeof document === "undefined") throw new Error("downloadFilteredCsv requires a DOM");
  const csv = transactionsToCsv(txs);
  // UTF-8 BOM so Excel detects the encoding correctly.
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  a.href = url;
  a.download = `transactions-${filterSummary(filter)}-${ts}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ─────────────────────────────────────────────────────────────────────
// Filter-option discovery
// ─────────────────────────────────────────────────────────────────────

/** Pull the distinct values for each filter dimension out of a tx set.
 *  Used to populate the dropdowns dynamically (we only show options that
 *  actually exist in the data, so the user never picks a dead-end filter). */
export function filterOptions(txs: Transaction[]): {
  categories: string[];
  subcategories: string[]; // "Category / Subcategory"
  portals: string[];
  accounts: string[];
  last4s: string[];
} {
  const categories = new Set<string>();
  const subcategories = new Set<string>();
  const portals = new Set<string>();
  const accounts = new Set<string>();
  const last4s = new Set<string>();
  for (const t of txs) {
    if (t.deleted) continue;
    if (t.category) categories.add(t.category);
    if (t.category && t.subcategory) {
      subcategories.add(`${t.category} / ${t.subcategory}`);
    }
    if (t.portal) portals.add(t.portal);
    if (t.account) accounts.add(t.account);
    if (t.account_last4) last4s.add(t.account_last4);
  }
  return {
    categories: [...categories].sort(),
    subcategories: [...subcategories].sort(),
    portals: [...portals].sort(),
    accounts: [...accounts].sort(),
    last4s: [...last4s].sort(),
  };
}
